// エラーハンドリングとユーザーフレンドリーなエラーメッセージの管理

import type {
  ApiErrorDetails,
  ApiErrorInfo,
  ErrorCode,
  ErrorContext,
  NetworkErrorInfo,
  ValidationErrorInfo,
} from '../types/error-types';
import { ErrorCodes } from '../types/error-types';
import { isApiError, isError, isRecord, isResponse, isString } from './type-guards';

export class APIError extends Error implements ApiErrorInfo {
  public override name: 'APIError' = 'APIError';

  constructor(
    public status: number,
    public code: ErrorCode,
    message: string,
    public details?: ApiErrorDetails,
    public requestId?: string,
  ) {
    super(message);
  }
}

export class NetworkError extends Error implements NetworkErrorInfo {
  public override name: 'NetworkError' = 'NetworkError';
  public type?: 'connection' | 'timeout' | 'dns' | 'unknown';

  constructor(message = 'ネットワークに接続できませんでした', type?: NetworkErrorInfo['type']) {
    super(message);
    this.type = type;
  }
}

export class ValidationError extends Error implements ValidationErrorInfo {
  public override name: 'ValidationError' = 'ValidationError';

  constructor(
    public field: string,
    message: string,
    public details?: ApiErrorDetails,
  ) {
    super(message);
  }
}

// エラーコードとユーザーフレンドリーメッセージのマッピング
const ERROR_MESSAGES: Record<string, string> = {
  // 認証関連
  AUTH_INVALID_CREDENTIALS: 'ログイン情報が正しくありません',
  AUTH_TOKEN_EXPIRED: 'セッションが期限切れです。再度ログインしてください',
  AUTH_UNAUTHORIZED: 'この操作を実行する権限がありません',
  AUTH_FORBIDDEN: 'アクセスが拒否されました',

  // バリデーション関連
  VALIDATION_REQUIRED: '必須項目が入力されていません',
  VALIDATION_INVALID_FORMAT: '入力形式が正しくありません',
  VALIDATION_TOO_LONG: '入力文字数が制限を超えています',
  VALIDATION_TOO_SHORT: '入力文字数が不足しています',

  // リソース関連
  RESOURCE_NOT_FOUND: '指定されたデータが見つかりません',
  RESOURCE_ALREADY_EXISTS: '既に存在するデータです',
  RESOURCE_LIMIT_EXCEEDED: '作成可能な数の上限に達しています',

  // サーバーエラー
  INTERNAL_SERVER_ERROR:
    'サーバーでエラーが発生しました。しばらく時間をおいてから再度お試しください',
  SERVICE_UNAVAILABLE: 'サービスが一時的に利用できません',
  RATE_LIMIT_EXCEEDED: 'アクセス数が上限に達しました。しばらく時間をおいてから再度お試しください',

  // ネットワーク関連
  NETWORK_ERROR: 'インターネット接続を確認してください',
  TIMEOUT_ERROR: '処理がタイムアウトしました。再度お試しください',

  // デフォルト
  UNKNOWN_ERROR: '予期しないエラーが発生しました',
};

// HTTPステータスコードからエラーコードを推測
function getErrorCodeFromStatus(status: number): ErrorCode {
  switch (status) {
    case 400:
      return ErrorCodes.VALIDATION_INVALID_FORMAT;
    case 401:
      return ErrorCodes.AUTH_UNAUTHORIZED;
    case 403:
      return ErrorCodes.AUTH_FORBIDDEN;
    case 404:
      return ErrorCodes.RESOURCE_NOT_FOUND;
    case 409:
      return ErrorCodes.RESOURCE_ALREADY_EXISTS;
    case 429:
      return ErrorCodes.RATE_LIMIT_EXCEEDED;
    case 500:
      return ErrorCodes.INTERNAL_SERVER_ERROR;
    case 503:
      return ErrorCodes.SERVICE_UNAVAILABLE;
    default:
      return ErrorCodes.UNKNOWN_ERROR;
  }
}

// ユーザーフレンドリーなメッセージを取得
export function getUserFriendlyMessage(code: ErrorCode, status?: number): string {
  const message = ERROR_MESSAGES[code];
  if (message) return message;

  // フォールバック: ステータスコードからメッセージを推測
  if (status) {
    const fallbackCode = getErrorCodeFromStatus(status);
    return ERROR_MESSAGES[fallbackCode] || ERROR_MESSAGES.UNKNOWN_ERROR || 'エラーが発生しました';
  }

  return ERROR_MESSAGES.UNKNOWN_ERROR || 'エラーが発生しました';
}

// APIエラーのハンドリング
export async function handleAPIError(error: unknown): Promise<APIError> {
  // 既にAPIErrorの場合はそのまま返す
  if (isApiError(error)) {
    return new APIError(error.status, error.code, error.message, error.details, error.requestId);
  }

  // ネットワークエラーの処理
  if (error instanceof TypeError && error.message.includes('fetch')) {
    throw new NetworkError('ネットワークに接続できませんでした', 'connection');
  }

  // Fetchエラーのレスポンスを解析
  if (isRecord(error)) {
    // Response オブジェクトの場合
    if ('response' in error && isResponse(error.response)) {
      const status = error.response.status || 500;
      let code: ErrorCode = ErrorCodes.UNKNOWN_ERROR;
      let message = '';
      let details: ApiErrorDetails | undefined;
      let requestId: string | undefined;

      try {
        const data = await error.response.json();
        if (isRecord(data) && 'error' in data && isRecord(data.error)) {
          if ('code' in data.error && isString(data.error.code)) {
            code = data.error.code as ErrorCode;
          } else {
            code = getErrorCodeFromStatus(status) as ErrorCode;
          }
          if ('message' in data.error && isString(data.error.message)) {
            message = data.error.message;
          } else {
            message = getUserFriendlyMessage(code, status);
          }
          if ('details' in data.error && isRecord(data.error.details)) {
            details = data.error.details;
          }
          if ('requestId' in data.error && isString(data.error.requestId)) {
            requestId = data.error.requestId;
          }
        } else {
          code = getErrorCodeFromStatus(status) as ErrorCode;
          message = getUserFriendlyMessage(code, status);
        }
      } catch {
        code = getErrorCodeFromStatus(status) as ErrorCode;
        message = getUserFriendlyMessage(code, status);
      }

      return new APIError(status, code, message, details, requestId);
    }

    // 直接的なエラーオブジェクトの場合
    if (
      'status' in error &&
      typeof error.status === 'number' &&
      'code' in error &&
      isString(error.code)
    ) {
      const code = error.code as ErrorCode;
      const message =
        'message' in error && isString(error.message)
          ? error.message
          : getUserFriendlyMessage(code, error.status);
      const details = 'details' in error && isRecord(error.details) ? error.details : undefined;
      const requestId =
        'requestId' in error && isString(error.requestId) ? error.requestId : undefined;

      return new APIError(error.status, code, message, details, requestId);
    }
  }

  // 不明なエラーの場合
  const message = isError(error) ? error.message : '不明なエラーが発生しました';
  return new APIError(500, ErrorCodes.UNKNOWN_ERROR, message);
}

// リトライ機能付きのAPIコール
export async function withRetry<T>(
  fn: () => Promise<T>,
  maxAttempts = 3,
  delay = 1000,
  backoffMultiplier = 2,
): Promise<T> {
  let lastError: Error | undefined;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      // リトライしないエラー（認証エラーなど）
      if (error instanceof APIError) {
        if (error.status === 401 || error.status === 403 || error.status === 404) {
          throw error;
        }
      }

      // 最後の試行の場合は例外を投げる
      if (attempt === maxAttempts) {
        throw lastError;
      }

      // 遅延してリトライ
      await new Promise((resolve) =>
        setTimeout(resolve, delay * backoffMultiplier ** (attempt - 1)),
      );
    }
  }

  if (lastError) {
    throw lastError;
  }
  throw new Error('Unknown error occurred');
}

// エラーレポーティング（開発時用）
export function reportError(error: Error, context?: ErrorContext) {
  if (process.env.NODE_ENV === 'development') {
    console.group('🚨 Error Report');
    console.error('Error:', error);
    if (context) {
      console.log('Context:', context);
    }
    if (error.stack) {
      console.log('Stack:', error.stack);
    }
    console.groupEnd();
  }

  // 本番環境ではエラートラッキングサービス（Sentry等）に送信
  // TODO: 本番環境でのエラートラッキング実装
}
